import {
  ButtonERP,
  DataTablePrimeReact,
  PaginationERP,
  TableERP,
} from "@finanedi.es/storybook"
import {
  faArrowLeft,
  faArrowRotateRight,
  faLock,
} from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useCuentaBancariaTesoreriaById } from "api/CuentaBancaria.hook"
import {
  deleteCuentaBancaria,
  getMovimientosBancariosExportarExcel,
  getMovimientosByCuentaBancaria,
  getSyncCuentaBancariaById,
  IbanMovimientosParamsProps,
} from "api/CuentaBancaria.service"
import { DocumentoConciliadoGetCuentaBancariaMovimientosDtoProps } from "api/DTOs/DocumentoConciliadoGetCuentaBancariaMovimientosDto"
import { MovimientoBancarioReadingDtoProps } from "api/DTOs/MovimientoBancarioReadingDto"
import { addNotaMovimientoBancario } from "api/MovimientoBancario.service"
import { ESTADO_CUENTA } from "api/Types/EstadoCuenta"
import { TIPO_CONCILIACION } from "api/Types/TipoConciliacion"
import { useUsuarioEmpresaActiva } from "api/Usuario.hook"
import Spinner from "common/components/Spinner"
import { handleDirectDownloadExcel } from "common/context/DownloadFileProvider"
import { CURRENT_DATE_FORMAT_WITH_HOURS_LONG } from "common/utils/constants"
import { formatDate } from "common/utils/date"
import { requestHandler } from "common/utils/utils"
import { DATE_YYYY_MM_DD } from "constants/formatoFechas"
import { PAGINATION_STYLES } from "constants/primereactStyles"
import { useAfterbanksNotifications } from "context/afterbankContext"
import { endOfMonth, format, startOfMonth } from "date-fns"
import {
  DateRangeModalFechas,
  ModalFechas,
} from "features/Contabilidad/pages/LibroDiario/components/Modales/ModalFechas"
import { IconAnnotationTemplate } from "features/Documentos/templates"
import { useFilterMenu } from "hooks/useFilterMenu"
import { Skeleton } from "primereact/skeleton"
import { Tooltip } from "primereact/tooltip"
import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useHistory, useParams } from "react-router-dom"
import * as ROUTES from "routes/RoutePaths"
import { frontendCurrency } from "utils/formatCurrency"
import { DEFAULT_BANK_LOGO_URL } from "../../constants"
import useModalAndCreate from "../../hooks/useModalAndCreate"
import { FilterMenu } from "./FilterMenu"
import ModalConciliacionMovimientos from "./components/ModalConciliadosMovimientos/ModalConciliadosMovimientos"
import { ModalDocumentosConciliados } from "./components/ModalDocumentosConciliados"
import { ModalEliminarCuenta } from "./components/ModalEliminarCuentaDetalle"
import { ModalNota } from "./components/ModalNota"
import { BANK_DETAIL_FILTERS, ConciliableWithForzosasLabel } from "./constants"
import {
  MOVIMIENTO_BANCARIO_STATUS,
  MOVIMIENTOS_BANCARIOS_COLUMNS,
} from "./movimientosBancariosColumns"

const BancosDetail = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const params = useParams()
  const currentMonthExportExcel = {
    min: startOfMonth(new Date()),
    max: endOfMonth(new Date()),
  }

  const [showModalConciliacion, setShowModalConciliacion] = useState<{
    show: boolean
    data: MovimientoBancarioReadingDtoProps | null
  }>({
    show: false,
    data: null,
  })
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<1 | 0 | -1 | null | undefined>(0)

  const [showModalDocumentosConciliados, setShowModalDocumentosConciliados] =
    useState(false)
  const [modalDocumentosConciliadosData, setModalDocumentosConciliadosData] =
    useState<Array<DocumentoConciliadoGetCuentaBancariaMovimientosDtoProps> | null>()
  const [date, setDate] = useState<DateRangeModalFechas>(
    currentMonthExportExcel
  )
  const [showExportDatesModal, setShowExportDatesModal] = useState(false)
  const {
    handleCreate,
    handleOpenCreateModal: handleCreateNotaModal,
    showCreateModal: showCreateNotaModal,
    handleCloseCreateModal,
  } = useModalAndCreate()

  const {
    cuentaBancariaById,
    cuentaBancariaByIdIsLoading,
    cuentaBancariaByIdIsFetching,
  } = useCuentaBancariaTesoreriaById(params.id)

  const { addNotification, getNotificationIdCompleted } =
    useAfterbanksNotifications()

  const { activeCompany } = useUsuarioEmpresaActiva()

  const defaultFilters = () => {
    const storedData = sessionStorage.getItem("bankDetailFilters")
    const defaultFilters = storedData ? JSON.parse(storedData) : {}
    return params.id !== defaultFilters?.cuentaBancariaId
      ? BANK_DETAIL_FILTERS(
          params?.id,
          activeCompany?.id,
          defaultFilters?.pagination?.itemsPerPage
        )
      : defaultFilters
  }

  const [bankDetailsFilter, setBankDetailsFilter] = useState(defaultFilters())
  const [showDelete, setShowDelete] = useState(false)

  const [sortingCriteria, setSortingCriteria] = useState<
    IbanMovimientosParamsProps["SortingCriteria"]
  >({
    sort: undefined,
    property: undefined,
  })
  const [pagination, setPagination] = useState(bankDetailsFilter.pagination)

  const dtRef =
    useRef<DataTablePrimeReact<MovimientoBancarioReadingDtoProps[]>>(null)
  const handleResetPagination = () => {
    dtRef.current && dtRef.current?.resetScroll()
    setPagination({
      itemsPerPage: pagination.itemsPerPage,
      skipCount: 0,
    })
  }

  const fetchMovimientos = useCallback(
    () => (queryParams: IbanMovimientosParamsProps) => {
      if (cuentaBancariaById?.iban) {
        return getMovimientosByCuentaBancaria(
          {
            ...queryParams,
            SortingCriteria: queryParams.SortingCriteria,
            SkipCount: queryParams?.SkipCount ?? 0,
            MaxResultCount: queryParams?.MaxResultCount ?? 10,
          },
          cuentaBancariaById?.iban
        )
      }
      return null
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cuentaBancariaById?.fechaUltimaActualizacion]
  )

  const { items, isLoading, totalCount, setServiceQuery, fetchTableData } =
    useFilterMenu({
      serviceCall: fetchMovimientos,
    })

  const handleRedirectToList = () => {
    history.push(ROUTES.BANCOS)
  }

  const handleOpenDeleteModal = () => {
    setShowDelete(!showDelete)
  }

  const exportToExcel = async () => {
    if (params.id) {
      const { data } = await getMovimientosBancariosExportarExcel(params.id, {
        FechaLowerLimit: date?.min ? format(date.min, DATE_YYYY_MM_DD) : "",
        FechaUpperLimit: date?.max ? format(date.max, DATE_YYYY_MM_DD) : "",
      })
      handleDirectDownloadExcel(data, `${t("exportarXlxs.bancosDetail")}.xlsx`)
    }
  }

  const handleDeleteCuentaBancaria = async () => {
    if (params.id) {
      requestHandler(
        params.id,
        deleteCuentaBancaria,
        handleRedirectToList,
        `${t("bancos.msg.deleteExit")}`,
        null,
        null,
        null
      )
      handleOpenDeleteModal()
    }
  }

  const onRenderNoteIcon = useCallback(
    (rowData: MovimientoBancarioReadingDtoProps) => {
      if (rowData?.status?.id === MOVIMIENTO_BANCARIO_STATUS.CONSOLIDADO) {
        return (
          <div>
            {IconAnnotationTemplate(
              rowData,
              rowData.nota,
              true,
              handleCreateNotaModal
            )}
          </div>
        )
      }
      return (
        <FontAwesomeIcon
          icon={faLock}
          className='tw-text-gray-300 tw-font-bold tw-h-5 tw-w-5'
        />
      )
    },

    [handleCreateNotaModal]
  )

  const onRenderConceptos = (rowData: MovimientoBancarioReadingDtoProps) => {
    const nombreMostrar =
      rowData?.importe && rowData.importe > 0
        ? rowData?.deudorNombre
        : rowData?.creditorNombre
    return (
      <div className='tw-flex tw-flex-col'>
        {nombreMostrar ? (
          <span className='tw-text-secondary'>{nombreMostrar}</span>
        ) : null}
        <span>{rowData.concepto}</span>
      </div>
    )
  }

  const onRenderCoincidenciasMovimientos = (
    props: MovimientoBancarioReadingDtoProps
  ) => {
    return (
      <>
        <Tooltip
          target={`.tooltip-info-${props.id}`}
          content={
            props.tipoConciliacion === TIPO_CONCILIACION.FORZADA
              ? `${t("nsAssets.filters.status.forzosas")}`
              : ""
          }
          position='right'
          className='tooltip-cartera-header tw-ml-2'
        />
        <span
          className={`tooltip-info-${props.id} tw-flex tw-flex-row tw-items-center tw-w-fit`}
        >
          {props.tipoConciliacion === TIPO_CONCILIACION.FORZADA && (
            <div className='!tw-mr-2 !tw-w-5 !tw-h-5 !tw-flex !tw-p-0 !tw-justify-center !tw-items-center !tw-border !tw-border-text-hyperlinks !tw-text-text-hyperlink !tw-rounded !tw-bg-transparent'>
              F
            </div>
          )}

          {ConciliableWithForzosasLabel(t, props, setShowModalConciliacion)}
        </span>
      </>
    )
  }

  const handleRefreshList = async () => {
    if (!cuentaBancariaById?.id || !cuentaBancariaById?.idExterno) return
    addNotification({
      id: cuentaBancariaById?.id,
      idExterno: cuentaBancariaById?.idExterno,
    })
    try {
      await getSyncCuentaBancariaById(cuentaBancariaById.id)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      getNotificationIdCompleted(cuentaBancariaById.id)
    }
  }

  const onSuccessCreateNota = () => {
    handleCloseCreateModal()
  }

  const handleCreateNota = async (values: { descripcion: string }) => {
    handleCreate(
      {
        idExterno: showCreateNotaModal?.data.idExterno,
        nota: values.descripcion,
      },
      addNotaMovimientoBancario,
      {
        onSuccessCreate: onSuccessCreateNota,
        onSuccessCreateMessage: `${t("bancos.msg.noteExit")}`,
      }
    )
    fetchTableData()
  }

  const handleRedirectToConciliaciones = () => {
    history.push(
      {
        pathname: ROUTES.CONCILIACION(params?.id),
        search: `?iban=${cuentaBancariaById?.iban}&nifTitular=${activeCompany?.nif}`,
      },
      {
        entidadBancaria: cuentaBancariaById?.entidadBancaria?.nombre,
        iban: cuentaBancariaById?.iban,
        nifTitular: activeCompany?.nif,
      }
    )
  }
  const onHideModalConciliacion = () => {
    setShowModalConciliacion({
      show: false,
      data: showModalConciliacion.data,
    })
  }

  useEffect(() => {
    if (cuentaBancariaById?.idExterno) {
      const updatedFilters = {
        ...bankDetailsFilter,
        idExterno: cuentaBancariaById.idExterno,
      }
      setBankDetailsFilter(updatedFilters)
      sessionStorage.setItem(
        "bankDetailFilters",
        JSON.stringify(updatedFilters)
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cuentaBancariaById?.idExterno])

  useEffect(() => {
    const updateFilters = {
      ...bankDetailsFilter,
      pagination,
    }
    sessionStorage.setItem("bankDetailFilters", JSON.stringify(updateFilters))
  }, [pagination, bankDetailsFilter])

  useEffect(() => {
    if (
      activeCompany.id &&
      activeCompany.id !== bankDetailsFilter.activeCompanyId
    ) {
      handleRedirectToList()
      sessionStorage.removeItem("bankDetailFilters")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany.id])

  const onSort = ({
    sortField,
    sortOrder,
  }: {
    sortField: string | undefined
    sortOrder: 1 | 0 | -1 | null | undefined
  }) => {
    setSortingCriteria({
      property: sortField ?? undefined,
      sort: sortOrder === 1 ? "ASC" : sortOrder === -1 ? "DESC" : undefined,
    })
    setSortField(sortField)
    setSortOrder(sortOrder)
    setPagination({
      itemsPerPage: pagination.itemsPerPage,
      skipCount: 0,
    })
    fetchTableData()
  }
  return (
    <div className='tw-flex tw-flex-col tw-order-2 tw-z-10 tw-relative tw-w-full tw-max-h-screen tw-h-full tw-bg-gray tw-overflow-x-hidden tw-overflow-y-hidden custom-scrollbar'>
      <div className='tw-flex-none tw-bg-white tw-shadow-md'>
        {cuentaBancariaByIdIsLoading ? (
          <Spinner />
        ) : (
          <div className='tw-flex tw-flex-col xl:tw-flex-row xl:tw-justify-between tw-p-4 tw-pt-6 tw-bg-white tw-shadow-3xl tw-mb-1'>
            <div className='tw-pr-2 tw-flex tw-items-center tw-gap-x-4'>
              <ButtonERP
                customize={{
                  type: "white",
                  size: "md",
                }}
                icon={<FontAwesomeIcon icon={faArrowLeft} />}
                onClick={() => handleRedirectToList()}
              />
              <img
                className={`${
                  cuentaBancariaById?.logo
                    ? "tw-h-11 tw-scale-125"
                    : "tw-w-11 tw-h-11"
                }`}
                src={cuentaBancariaById?.logo || DEFAULT_BANK_LOGO_URL}
                alt=''
              />
              <div className='tw-grid tw-grid-cols-1 tw-items-center tw-text-nowrap tw-text-ellipsis tw-overflow-hidden tw-whitespace-nowrap tw-flex-1'>
                <span className='tw-w-full tw-flex-1 tw-truncate tw-overflow-hidden tw-text-lg tw-font-medium tw-text-left'>
                  {cuentaBancariaById?.nombre
                    ? `${cuentaBancariaById.nombre} - ${cuentaBancariaById?.entidadBancaria?.nombre}`
                    : cuentaBancariaById?.entidadBancaria?.nombre}
                </span>
                <span className='tw-w-full tw-truncate tw-overflow-hidden tw-text-base tw-text-left tw-font-medium tw-text-text-50'>
                  {cuentaBancariaById?.iban}
                  {cuentaBancariaById?.estado === ESTADO_CUENTA?.VALIDADA ? (
                    <FontAwesomeIcon
                      icon={["fal", "check-circle"]}
                      className='ml-2 tw-text-secondary'
                    />
                  ) : null}
                </span>
              </div>
            </div>
            <div className='tw-pl-4 tw-pt-4 xl:tw-p-0'>
              <div className='tw-flex tw-flex-row tw-justify-start tw-items-center tw-text-nowrap tw-text-ellipsis tw-overflow-hidden tw-whitespace-nowrap tw-flex-1 tw-w-full'>
                <div className='tw-flex tw-flex-col tw-w-fit tw-px-10 xl:tw-border-l'>
                  <div className='tw-w-full tw-truncate tw-overflow-hidden tw-text-sm tw-text-left'>
                    {`${t("bancos.idView.updateLast")}: `}
                    <span className='tw-font-medium'>
                      {cuentaBancariaByIdIsFetching ? (
                        <Skeleton
                          height='15px'
                          width='154px'
                          className='tw-inline-block'
                        />
                      ) : cuentaBancariaById?.fechaUltimaActualizacion ? (
                        formatDate(
                          cuentaBancariaById?.fechaUltimaActualizacion,
                          CURRENT_DATE_FORMAT_WITH_HOURS_LONG
                        )
                      ) : (
                        "-"
                      )}
                    </span>
                  </div>
                  <div className='tw-w-full tw-truncate tw-overflow-hidden tw-text-sm tw-text-left'>
                    {`${t("bancos.idView.labelDateRenewal")}: `}
                    <span className='tw-text-text'>
                      {cuentaBancariaByIdIsFetching ? (
                        <Skeleton
                          height='15px'
                          width='75px'
                          className='tw-inline-block'
                        />
                      ) : cuentaBancariaById?.fechaRenovacion ? (
                        formatDate(cuentaBancariaById?.fechaRenovacion)
                      ) : (
                        "-"
                      )}
                    </span>
                  </div>
                </div>
                <ButtonERP
                  onClick={() =>
                    !cuentaBancariaById?.necesitaValidacion &&
                    handleRefreshList()
                  }
                  loading={
                    cuentaBancariaById?.id
                      ? getNotificationIdCompleted(cuentaBancariaById.id)
                      : false
                  }
                  disabled={
                    cuentaBancariaById?.id
                      ? getNotificationIdCompleted(cuentaBancariaById.id)
                      : false
                  }
                  icon={<FontAwesomeIcon icon={faArrowRotateRight} />}
                  className='tw-mr-10'
                  customize={{
                    type: "white",
                    size: "md",
                  }}
                  label={`${t("bancos.idView.updateButton")}`}
                />
                {cuentaBancariaById?.necesitaValidacion ? (
                  <Tooltip
                    target='.tooltip-ref'
                    content={`${t("bancos.idView.msgTooltip")}`}
                    position='bottom'
                  />
                ) : null}
                <div className='tw-flex tw-flex-col tw-min-w-72.5 tw-px-10 xl:tw-pr-2 tw-border-l'>
                  <div className='tw-flex tw-justify-between tw-items-center tw-gap-x-2'>
                    <span className='tw-w-full tw-text-base tw-text-left'>
                      {`${t("bancos.idView.labelAvailableBalance")}: `}
                    </span>
                    <span className='tw-font-medium tw-text-xl'>
                      {cuentaBancariaByIdIsFetching ? (
                        <Skeleton
                          height='28px'
                          width='95px'
                        />
                      ) : (
                        frontendCurrency(
                          cuentaBancariaById?.saldos?.disponible ?? 0
                        )
                      )}
                    </span>
                  </div>

                  <div className='tw-flex tw-justify-between tw-items-center tw-gap-x-2'>
                    <span className='tw-w-full tw-text-sm tw-text-left'>
                      {`${t("bancos.idView.labelCurrentBalance")}: `}
                    </span>
                    <span className='tw-font-normal tw-text-sm'>
                      {cuentaBancariaByIdIsFetching ? (
                        <Skeleton
                          height='15px'
                          width='70px'
                          className='tw-inline-block'
                        />
                      ) : cuentaBancariaById?.saldos?.contable ? (
                        frontendCurrency(cuentaBancariaById.saldos.contable)
                      ) : (
                        "-"
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <FilterMenu
          handleRedirectToConciliaciones={handleRedirectToConciliaciones}
          handleOpenDeleteModal={handleOpenDeleteModal}
          serviceQuery={setServiceQuery}
          sortingCriteria={sortingCriteria}
          skipCount={pagination.skipCount}
          maxResultCount={pagination.itemsPerPage}
          resetPagination={handleResetPagination}
          exportToExcel={() => setShowExportDatesModal(true)}
          bankDetailsFilter={bankDetailsFilter}
          setBankDetailsFilter={setBankDetailsFilter}
        />
      </div>

      <div className='tw-flex tw-flex-col tw-h-[calc(100%-164px)] tw-relative '>
        <div className='tw-h-[calc(100%-60px)] tw-min-h-0'>
          <TableERP
            refTable={dtRef}
            columns={MOVIMIENTOS_BANCARIOS_COLUMNS({
              onRenderNoteIcon,
              onRenderCoincidenciasMovimientos,
              onRenderConceptos,
              t,
            })}
            className='tw-h-full !tw-z-0'
            columnResizeMode='expand'
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={onSort}
            removableSort
            value={items}
            showGridlines
            lazy
            dataKey='idExterno'
            scrollable
            size='small'
            loading={isLoading}
            emptyMessage={t("primeReact.table.emptyMessage")}
          />
        </div>
        <PaginationERP
          className={PAGINATION_STYLES}
          totalRecords={totalCount}
          first={pagination.skipCount}
          rows={pagination.itemsPerPage}
          resetScroll={() => dtRef.current && dtRef.current?.resetScroll()}
          setPagination={setPagination}
          optionDropdown={[10, 20, 50, 100]}
          texts={{
            textRowsPerPageDropdown: `${t("pagination.textRowsPag")}`,
            textCurrentPageReport: `${t("pagination.from")}`,
          }}
        />
      </div>
      <ModalEliminarCuenta
        isOpen={showDelete}
        onCancel={() => {
          handleOpenDeleteModal()
        }}
        onConfirm={handleDeleteCuentaBancaria}
      />
      <ModalNota
        isOpen={showCreateNotaModal.status}
        onHide={handleCloseCreateModal}
        data={showCreateNotaModal.data}
        handleSubmitAnnotation={handleCreateNota}
      />
      <ModalDocumentosConciliados
        isOpen={showModalDocumentosConciliados}
        data={modalDocumentosConciliadosData}
        onCancel={() => {
          setShowModalDocumentosConciliados(false)
          setTimeout(() => {
            setModalDocumentosConciliadosData([])
          }, 300)
        }}
      />

      <ModalConciliacionMovimientos
        showModal={showModalConciliacion.show}
        data={showModalConciliacion.data}
        onHideModalConciliacion={onHideModalConciliacion}
        fetchTable={fetchTableData}
      />

      <ModalFechas
        isOpen={showExportDatesModal}
        date={date}
        setDate={setDate}
        onCancel={() => {
          setShowExportDatesModal(false)
          setDate(currentMonthExportExcel)
        }}
        onConfirm={exportToExcel}
        text={`${t("diario.exportModal.dateRange")}`}
      />
    </div>
  )
}

export default BancosDetail
